/**
 * Citation resolver service (PROD-590).
 *
 * Wraps `retrieveSimilar` (PROD-574) with the threshold + fallback policy
 * dictated by HC3 + HC8. Every NBA candidate that surfaces in ClinicalDesk
 * resolves a `CitationSet` through this module before render. The resolver
 * is the single chokepoint where Live vs Demo routing happens; downstream
 * UI never branches on `mode` directly.
 *
 * Contract per HC3 + HC8:
 *   - Live: call `retrieveSimilar(buildQuery(candidate), 3, 0.78)` against
 *     the corpus, surface up to k chunks above threshold, and emit a
 *     `corpus_retrieval` telemetry event with the chunk_ids + distances.
 *     A hard failure (deps missing, retrieval throws, zero matches above
 *     threshold) returns `source: 'empty'` with an explicit `emptyReason`.
 *   - Demo: short-circuit retrieval and synthesize a deterministic
 *     `RetrievedChunk[]` from the candidate's fixture citations. No corpus
 *     call is made; the telemetry event still fires with `mode: 'demo'`
 *     and `source: 'demo'` so the dashboard sees Demo volume separately.
 *
 * HC3 invariant (no NBA without a citation): the resolver never silently
 * returns nothing. Every call yields a `CitationSet` whose `source` is one
 * of `'live' | 'demo' | 'empty'`. The consumer (NbaCard) renders the
 * empty-state inline rather than hiding the card; the Override path stays
 * available so the clinician can act and capture the gap.
 *
 * HC8 corpus-as-floor: corpus citations are deterministic ground truth.
 * The resolver does NOT mutate retrieved chunk metadata before surfacing
 * it; the chunk text + provenance reaches the drawer verbatim.
 *
 * HC9 telemetry-real: every invocation emits exactly one
 * `corpus_retrieval` envelope. Tests assert the payload shape via
 * `vi.mock('../services/telemetry')`.
 *
 * HC5: zero em / en dashes anywhere.
 */
import {
  retrieveSimilar,
  type RetrievalDeps,
  type RetrievedChunk,
} from './corpus-retrieval';
import { emitTelemetry, newCorrelationId } from './telemetry';
import type { NbaCardCandidate } from '../components/NbaCard';
import type { Mode } from '../state/types';
import type { Citation } from '../data/synthetic-patients/types';

/**
 * Default retrieval shape per the PROD-590 ticket. `k = 3` and
 * `threshold = 0.78` are the verbatim values from the AC.
 */
export const DEFAULT_K = 3;
export const DEFAULT_THRESHOLD = 0.78;

/**
 * Result envelope. The `source` discriminator is the visible contract that
 * downstream UI switches on; the `chunks` array is the citation list.
 *
 *   - `'live'`  - chunks were retrieved from the live corpus
 *   - `'demo'`  - chunks were synthesized from the candidate fixture
 *   - `'empty'` - retrieval (Live or Demo) produced zero usable chunks;
 *                 `emptyReason` carries the visible explanation
 */
export interface CitationSet {
  source: 'live' | 'demo' | 'empty';
  chunks: RetrievedChunk[];
  emptyReason: string | null;
}

/**
 * Caller-supplied options. The resolver is transitively pure given these
 * dependencies; tests inject mocks for both retrieval + clock.
 */
export interface ResolveCitationsOptions {
  /** Live retrieval deps. May be `null` in Demo Mode (the resolver short-circuits). */
  deps?: RetrievalDeps | null;
  /** Override the retrieval k. Default `DEFAULT_K` (3). */
  k?: number;
  /** Override the relevance threshold. Default `DEFAULT_THRESHOLD` (0.78). */
  threshold?: number;
  /** Injectable clock for deterministic telemetry timestamps. */
  now?: () => string;
  /**
   * Injectable retrieval function (test mode). Defaults to the real
   * `retrieveSimilar` import. Tests can swap in a controlled fake without
   * stubbing the whole module.
   */
  retriever?: typeof retrieveSimilar;
  /** Injectable correlation id minter (test mode). */
  newCorrelationId?: typeof newCorrelationId;
  /** Telemetry hook for tests; defaults to the real `emitTelemetry`. */
  emit?: typeof emitTelemetry;
}

/**
 * Build the retrieval query string for an NBA candidate. The shape is
 * intentionally compact: pathway + step ids ground the query in the
 * pathway's clinical scope, while the title supplies the actionable
 * fragment. The rationale text is appended truncated so corpus chunks
 * keyed on rationale terms are recall-friendly without bloating the
 * embedding context.
 *
 * Pure function; safe to call from tests without spinning up retrieval.
 */
export function buildQuery(candidate: NbaCardCandidate): string {
  const titleFragment = candidate.title.trim();
  const rationaleFragment = candidate.rationale.slice(0, 240).trim();
  return [
    candidate.pathwayId,
    candidate.stepId,
    titleFragment,
    rationaleFragment,
  ]
    .filter((s) => s.length > 0)
    .join(' | ');
}

/**
 * Convert a fixture `Citation` to a `RetrievedChunk`. Demo Mode synthesizes
 * a deterministic distance of 0 (identical-vector convention) and a
 * relevance of 1 so the strip + drawer treat the citation the same way as
 * a Live chunk. The `tier` defaults to `tier-fixture` so the drawer can
 * surface the demo provenance in plain English.
 *
 * Pure function; deterministic.
 */
export function citationToDemoChunk(
  citation: Citation,
  retrievedAt: string,
): RetrievedChunk {
  return {
    chunk_id: `demo-${citation.id}`,
    source_id: citation.url ?? citation.id,
    source_citation: `${citation.title} (${citation.year})`,
    section: null,
    tier: 'tier-fixture',
    text: `${citation.title} (${citation.year}) - fixture citation surfaced via Demo Mode. The Live corpus chunk text is loaded only when the active mode is Live.`,
    relevance_score: 1,
    retrieved_at: retrievedAt,
  };
}

function timestamp(now: ResolveCitationsOptions['now']): string {
  if (now) return now();
  return new Date().toISOString();
}

function emitCorpusRetrieval(args: {
  candidate: NbaCardCandidate;
  patientId: string;
  mode: Mode;
  k: number;
  threshold: number;
  result: CitationSet;
  latencyMs: number;
  ts: string;
  correlationId: string;
  emit: typeof emitTelemetry;
}): void {
  const { candidate, patientId, mode, k, threshold, result, latencyMs, ts, correlationId, emit } =
    args;
  emit('corpus_retrieval', {
    pathway_id: candidate.pathwayId,
    step_id: candidate.stepId,
    tenant_id: candidate.tenantId,
    patient_id: patientId,
    nba_id: candidate.id,
    mode,
    k,
    threshold,
    chunk_ids: result.chunks.map((c) => c.chunk_id),
    // Distances are derived from `relevance_score` via `1 - r`, clamped at
    // [0, 2] to satisfy the schema. Demo chunks land at distance 0.
    distances: result.chunks.map((c) => Math.max(0, Math.min(2, 1 - c.relevance_score))),
    result_count: result.chunks.length,
    latency_ms: latencyMs,
    source: result.source,
    empty_reason: result.emptyReason,
    correlation_id: correlationId,
    ts,
  });
}

/**
 * Demo Mode resolution. Reads the candidate's fixture citations and maps
 * each to a synthetic `RetrievedChunk`. If the fixture itself carries no
 * citations, the result surfaces `source: 'empty'` with an explicit
 * reason so the empty-state copy in the strip explains the gap rather
 * than hiding it (HC3).
 */
export function resolveDemoCitations(
  candidate: NbaCardCandidate,
  ts: string,
): CitationSet {
  if (candidate.citations.length === 0) {
    return {
      source: 'empty',
      chunks: [],
      emptyReason:
        'No fixture citations attached to this NBA candidate. Switch to Live Mode or update the fixture.',
    };
  }
  return {
    source: 'demo',
    chunks: candidate.citations.map((c) => citationToDemoChunk(c, ts)),
    emptyReason: null,
  };
}

/**
 * Resolve the citation set for an NBA candidate. The caller passes the
 * active mode, the candidate, and (optionally) the live retrieval deps.
 *
 * Live Mode flow:
 *   1. If `deps` is null, return `source: 'empty'` with a credentials
 *      reason. No retrieval call. No throw.
 *   2. Otherwise call the (injectable) retriever with k + threshold from
 *      options or the documented defaults.
 *   3. If the retriever throws OR returns `[]`, return `source: 'empty'`
 *      with the appropriate reason. The caller's UI surfaces the
 *      "no supporting evidence above threshold" empty state per HC3.
 *   4. Otherwise return `source: 'live'` with the retrieved chunks.
 *
 * Demo Mode flow:
 *   1. Short-circuit retrieval. Synthesize chunks from the candidate's
 *      fixture citations via `resolveDemoCitations`. Telemetry still fires
 *      so the dashboard sees Demo volume.
 *
 * Telemetry: exactly one `corpus_retrieval` envelope per call.
 */
export async function resolveCitationsForNba(
  candidate: NbaCardCandidate,
  patientId: string,
  mode: Mode,
  options: ResolveCitationsOptions = {},
): Promise<CitationSet> {
  const k = options.k ?? DEFAULT_K;
  const threshold = options.threshold ?? DEFAULT_THRESHOLD;
  const emit = options.emit ?? emitTelemetry;
  const mintCorr = options.newCorrelationId ?? newCorrelationId;
  const ts = timestamp(options.now);
  const correlationId = mintCorr();
  const start = typeof performance !== 'undefined' ? performance.now() : Date.now();

  if (mode === 'demo') {
    const demoSet = resolveDemoCitations(candidate, ts);
    const latencyMs = Math.max(
      0,
      Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - start),
    );
    emitCorpusRetrieval({
      candidate,
      patientId,
      mode,
      k,
      threshold,
      result: demoSet,
      latencyMs,
      ts,
      correlationId,
      emit,
    });
    return demoSet;
  }

  // Live Mode path.
  if (!options.deps) {
    const empty: CitationSet = {
      source: 'empty',
      chunks: [],
      emptyReason: 'Live retrieval unavailable. Corpus credentials missing.',
    };
    const latencyMs = Math.max(
      0,
      Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - start),
    );
    emitCorpusRetrieval({
      candidate,
      patientId,
      mode,
      k,
      threshold,
      result: empty,
      latencyMs,
      ts,
      correlationId,
      emit,
    });
    return empty;
  }

  const retriever = options.retriever ?? retrieveSimilar;
  const query = buildQuery(candidate);

  let chunks: RetrievedChunk[] = [];
  let emptyReason: string | null = null;
  try {
    chunks = await retriever(query, options.deps, { k, threshold });
  } catch (err) {
    chunks = [];
    emptyReason = `Live retrieval threw: ${err instanceof Error ? err.message : String(err)}`;
  }

  const latencyMs = Math.max(
    0,
    Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - start),
  );

  let result: CitationSet;
  if (chunks.length > 0) {
    result = { source: 'live', chunks, emptyReason: null };
  } else {
    result = {
      source: 'empty',
      chunks: [],
      emptyReason:
        emptyReason ??
        `No corpus chunks above threshold ${threshold.toFixed(2)} for query "${query.slice(0, 80)}".`,
    };
  }

  emitCorpusRetrieval({
    candidate,
    patientId,
    mode,
    k,
    threshold,
    result,
    latencyMs,
    ts,
    correlationId,
    emit,
  });

  return result;
}
